import { Link } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { FAMILY, ProgressBar, TagChip } from '@/components/canvas/bits';

const STATUS_CLS = {
  Confirmed: 'bg-success-muted text-success border border-success-border',
  'In progress': 'bg-warning-muted text-warning border border-warning-border',
  'Not started': 'bg-muted text-muted-foreground border border-transparent',
};

function CellValue({ cell }) {
  if (cell.tag) return <TagChip tag={cell.tag} />;
  if (!cell.value) return <span className="text-muted-foreground">-</span>;
  return <span className={cn('whitespace-pre-line', cell.calc && 'font-semibold tabular-nums')}>{cell.value}</span>;
}

function TableView({ b }) {
  if (b.rows.length === 0) {
    return (
      <div>
        {b.title && <h5 className="mb-1.5 text-sm font-semibold">{b.title}</h5>}
        <p className="text-sm italic text-muted-foreground">No rows filled in.</p>
      </div>
    );
  }
  if (b.mode === 'table') {
    return (
      <div className="space-y-1.5">
        {b.title && <h5 className="text-sm font-semibold">{b.title}</h5>}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[32rem] border-collapse text-left text-xs leading-relaxed">
            <thead>
              <tr className="bg-muted/60">
                <th scope="col" className="px-2.5 py-2 font-semibold">{b.rowLabel || ''}</th>
                {b.columns.map((c) => (
                  <th key={c.label} scope="col" className="px-2.5 py-2 font-semibold">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={`${r.label}-${i}`} className="border-t border-border align-top">
                  <th scope="row" className="px-2.5 py-2 font-semibold">{r.label}</th>
                  {r.cells.map((cell, j) => (
                    <td key={`${i}-${j}`} className="px-2.5 py-2">
                      <CellValue cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {b.title && <h5 className="text-sm font-semibold">{b.title}</h5>}
      {b.rows.map((r, i) => (
        <div key={`${r.label}-${i}`} className="rounded-lg border border-border p-3">
          <p className="text-sm font-semibold">{r.label}</p>
          {r.sub && <p className="text-xs text-muted-foreground">{r.sub}</p>}
          <dl className="mt-2 grid gap-x-4 gap-y-2 md:grid-cols-2">
            {b.columns.map((c, j) => {
              const cell = r.cells[j];
              if (!cell || (!cell.value && !cell.tag)) return null;
              return (
                <div key={`${i}-${c.label}`} className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</dt>
                  <dd className="text-sm leading-relaxed">
                    <CellValue cell={cell} />
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
    </div>
  );
}

function BlockView({ b }) {
  if (b.kind === 'kv') {
    return (
      <dl className="grid gap-x-6 gap-y-2.5 md:grid-cols-2">
        {b.rows.map((r) => (
          <div key={r.label} className="min-w-0">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{r.label}</dt>
            <dd className="text-sm leading-relaxed">
              <CellValue cell={r} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  if (b.kind === 'table') return <TableView b={b} />;
  if (b.kind === 'pull') {
    return (
      <div className="rounded-lg bg-muted/50 p-3">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{b.title}</p>
        <dl className="space-y-1.5">
          {b.rows.map((r) => (
            <div key={r.label}>
              <dt className="text-xs font-semibold">{r.label}</dt>
              <dd className="whitespace-pre-line text-sm">{r.value || <span className="text-muted-foreground">-</span>}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }
  if (b.kind === 'note') {
    return <p className="rounded-lg border border-warning-border bg-warning-muted p-3 text-sm">{b.text}</p>;
  }
  return null;
}

function CanvasSection({ c, linkTo }) {
  const fam = FAMILY[c.family] || FAMILY.cyan;
  const hasResults = c.results.some((r) => r.value);
  const hasEntry = c.signoff.entry.some((e) => e.value);
  return (
    <Collapsible className="rounded-xl border border-border bg-card">
      <CollapsibleTrigger className="group flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl font-heading text-base font-semibold text-white', fam.solid)}>
          {c.no === 0 ? <ApperIcon name="IdCard" size={18} /> : c.no}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-heading text-sm font-semibold">{c.title}</span>
          <span className="block truncate text-xs text-muted-foreground">{c.question}</span>
        </span>
        <span className={cn('hidden rounded-full px-2 py-0.5 text-xs font-medium sm:inline', STATUS_CLS[c.stale ? 'In progress' : c.status])}>
          {c.stale ? 'Review' : c.status}
        </span>
        <ApperIcon name="ChevronDown" size={16} className="shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-5 border-t border-border p-4">
          {c.status === 'Not started' && <p className="text-sm italic text-muted-foreground">Not started yet.</p>}
          {hasResults && (
            <dl className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-px overflow-hidden rounded-xl bg-sidebar-border">
              {c.results.map((r) => (
                <div key={r.id} className="bg-sidebar px-3.5 py-3 text-sidebar-foreground">
                  <dt className="text-[10px] font-semibold uppercase tracking-wider opacity-75">{r.label}</dt>
                  <dd className="font-heading text-lg font-semibold leading-tight text-highlight">{r.value || '-'}</dd>
                </div>
              ))}
            </dl>
          )}
          {c.steps.map((st) => (
            <div key={st.id} className="space-y-3">
              <h4 className="flex items-center gap-2 font-heading text-sm font-semibold">
                <span className={cn('flex size-5 items-center justify-center rounded-md text-[11px] text-white', fam.solid)}>{st.n}</span>
                {st.title}
              </h4>
              {st.blocks.map((b, i) => (
                <BlockView key={`${st.id}-${i}`} b={b} />
              ))}
            </div>
          ))}
          {hasEntry && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {c.signoff.mode === 'panel' ? 'Panel round' : 'Sceptic round'}: {c.signoff.sceptic}
              </h4>
              <dl className="grid gap-x-6 gap-y-2 md:grid-cols-2">
                {c.signoff.entry
                  .filter((e) => e.value)
                  .map((e) => (
                    <div key={e.label}>
                      <dt className="text-[11px] font-semibold text-muted-foreground">{e.label}</dt>
                      <dd className="whitespace-pre-line text-sm">{e.value}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}
          {linkTo && (
            <Link to={linkTo(c.no)} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              Open {c.no === 0 ? 'the Case Card' : `Canvas ${c.no}`}
              <ApperIcon name="ArrowRight" size={14} />
            </Link>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Appendix({ appendix }) {
  const { gaps, ledger } = appendix;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold">
          <ApperIcon name="SearchX" size={16} className="text-highlight" />
          Evidence Gaps list
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{gaps.length}</span>
        </h3>
        {gaps.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">No numbers are tagged E or G.</p>
        ) : (
          <ul className="divide-y divide-border">
            {gaps.map((g, i) => (
              <li key={`${g.item}-${i}`} className="flex items-start gap-2.5 py-2">
                <TagChip tag={g.tag} className="mt-0.5" />
                <div className="min-w-0 text-sm">
                  <p className="leading-snug">{g.item}</p>
                  <p className="text-xs text-muted-foreground">{g.where}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold">
          <ApperIcon name="MessageSquareWarning" size={16} className="text-primary" />
          Buy-in Ledger
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{ledger.length}</span>
        </h3>
        {ledger.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">No objections logged yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {ledger.map((r, i) => (
              <li key={`${r.where}-${i}`} className="space-y-0.5 py-2 text-sm">
                <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{r.where}</span>
                  <span>{r.sceptic}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5">{r.status}</span>
                  {r.star === 'Yes' && <span className="rounded bg-highlight px-1.5 py-0.5 font-semibold text-highlight-foreground">Raise first</span>}
                </p>
                <p className="leading-snug">{r.objection}</p>
                {r.evidence && <p className="text-xs text-muted-foreground">Evidence: {r.evidence}</p>}
                {r.owner && <p className="text-xs text-muted-foreground">Follow-up: {r.owner}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// the finished pack on screen: cover facts, index, every canvas, and the two lists at the back
export default function PackView({ model, linkTo }) {
  const cs = model.caseSummary;
  const facts = [
    ['Journey', cs.journey],
    ['Function', cs.function],
    ['Sponsor', cs.sponsor],
    ['Sponsor KPIs', cs.kpis],
    ['The AI idea', cs.idea],
  ].filter((x) => x[1]);
  return (
    <div className="space-y-6">
      {facts.length > 0 && (
        <section className="rounded-2xl border border-border bg-card p-4 shadow-xs md:p-5">
          <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">The case in brief</h3>
          <dl className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            {facts.map(([k, val]) => (
              <div key={k} className="min-w-0">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{k}</dt>
                <dd className="text-sm leading-relaxed">{val}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <section className="space-y-2">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">The canvases</h3>
        {model.canvases.map((c) => (
          <CanvasSection key={c.no} c={c} linkTo={linkTo} />
        ))}
      </section>
      <Appendix appendix={model.appendix} />
    </div>
  );
}

export function IndexStrip({ model }) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
      {model.index.map((r) => (
        <li key={r.no} className="space-y-1.5 rounded-xl border border-border bg-card p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-heading text-sm font-semibold">{r.no === 0 ? 'Case Card' : `Canvas ${r.no}`}</span>
            <span className={cn('rounded-full px-1.5 py-0.5 text-[11px] font-medium', STATUS_CLS[r.stale ? 'In progress' : r.status])}>
              {r.stale ? 'Review' : r.status}
            </span>
          </div>
          <p className="truncate text-xs text-muted-foreground">{r.title}</p>
          <ProgressBar value={parseInt(r.completion, 10) || 0} label={`${r.title} completion`} />
        </li>
      ))}
    </ul>
  );
}
